"use client";

import { Button } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { downloadCsv, toCsv } from "@loot-os/ui/lib/csv";
import {
  type DataTableFeatures,
  type DataTableFilterOperator,
  dataTableFeatures,
  NUMBER_FILTER_OPERATORS,
  TEXT_FILTER_OPERATORS,
} from "@loot-os/ui/lib/dataTable";
import { cn } from "@loot-os/ui/lib/utils";
import {
  type Column,
  type ColumnDef,
  type ColumnPinningState,
  type Row,
  type RowData,
  type RowSelectionState,
  type TableState,
  type Table as TanstackTable,
  useTable,
} from "@tanstack/react-table";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronsUpDownIcon,
  DownloadIcon,
  FilterIcon,
  PlusIcon,
  XIcon,
} from "lucide-react";
import { type CSSProperties, type ReactNode, useMemo, useState } from "react";

/**
 * The dashboard's table: sortable headers, a search box, a dropdown per `meta.filter: "select"`
 * column, a CSV download of what is shown, and columns pinned to either edge so an action stays
 * in view while the rest scrolls. Retool's table, on the shadcn primitives.
 *
 * Columns come from `createDataTableColumns<TData>()` (`@loot-os/ui/lib/dataTable`), declared at
 * module scope so their identity is stable. Sorting and filtering are the table's own state;
 * selection is the caller's when `rowSelection` is passed.
 */

const EMPTY_SELECTION: RowSelectionState = {};

/**
 * What one filter row stores on its column. One column can carry several, so the value is an
 * array and the column's filter function reads it; a built-in function would see the array.
 */
interface ColumnFilter {
  id: string;
  operator: DataTableFilterOperator;
  /** What the user typed. Dollars for a `filterCents` column; the function converts to cents. */
  input: string;
}

/**
 * A column's filter function. Every active filter on the column must hold; an operator that
 * needs a value and has none yet keeps every row, so a half-built filter hides nothing.
 */
const dataTableFilterFn = Object.assign(
  <TData extends RowData>(
    row: Row<DataTableFeatures, TData>,
    columnId: string,
    value: unknown,
  ): boolean => {
    const cents = row.getAllCells().find((cell) => cell.column.id === columnId)?.column.columnDef
      .meta?.filterCents;
    return columnFilters(value).every(
      (filter) =>
        (filter.operator.value !== "empty" && filter.input.trim() === "") ||
        matchesFilter(row.getValue(columnId), filter, cents === true),
    );
  },
  { autoRemove: (value: unknown) => columnFilters(value).length === 0 },
);

function columnFilters(value: unknown): ColumnFilter[] {
  return Array.isArray(value) ? (value as ColumnFilter[]) : [];
}

/** True when the row's value satisfies the filter. Numbers compare numerically, text as strings. */
function matchesFilter(raw: unknown, filter: ColumnFilter, cents: boolean): boolean {
  const { operator, input } = filter;
  if (operator.value === "number") {
    const number = typeof raw === "number" ? raw : Number(raw);
    const against = cents ? Math.round(Number(input) * 100) : Number(input);
    if (operator.id === "eq") return number === against;
    if (operator.id === "gt") return number > against;
    if (operator.id === "gte") return number >= against;
    if (operator.id === "lt") return number < against;
    return number <= against;
  }
  const text = raw === null || raw === undefined ? "" : String(raw);
  if (operator.value === "empty") return operator.fn === "equals" ? text === "" : text !== "";
  if (operator.fn === "equals") return text.toLowerCase() === input.toLowerCase();
  if (operator.fn === "startsWith") return text.toLowerCase().startsWith(input.toLowerCase());
  return text.toLowerCase().includes(input.toLowerCase());
}

export interface DataTableProps<TData extends RowData> {
  columns: ColumnDef<DataTableFeatures, TData, unknown>[];
  data: TData[];
  /** Stable row identity; selection and React keys use it. Defaults to the row index. */
  getRowId?: (row: TData) => string;
  /** Column ids held at the leading or trailing edge. */
  pinned?: ColumnPinningState;
  /** Controlled selection; the selection column reads and writes it. */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (next: RowSelectionState) => void;
  /** Placeholder of the search box; no search box when `false`. */
  search?: string | false;
  /** Base name of the download, without `.csv`; no download button when absent. */
  csvName?: string;
  /** Extra controls at the toolbar's trailing end. */
  toolbar?: ReactNode;
  emptyMessage?: string;
  /**
   * A CSS length that caps the table's height (`"32rem"`, `"calc(100dvh - 20rem)"`). Past it the
   * rows scroll under a header that stays in view; the toolbar sits outside the scroll and
   * stays put either way. Unset, the table grows with its rows and the page scrolls.
   */
  maxHeight?: CSSProperties["maxHeight"];
  className?: string;
}

function pinnedStyle<TData extends RowData>(
  column: Column<DataTableFeatures, TData, unknown>,
): CSSProperties {
  const pinned = column.getIsPinned();
  if (!pinned) return {};
  return {
    position: "sticky",
    insetInlineStart: pinned === "start" ? `${column.getStart("start")}px` : undefined,
    insetInlineEnd: pinned === "end" ? `${column.getAfter("end")}px` : undefined,
    width: `${column.getSize()}px`,
    minWidth: `${column.getSize()}px`,
    zIndex: 1,
  };
}

function pinnedClass<TData extends RowData>(
  column: Column<DataTableFeatures, TData, unknown>,
  pinning: ColumnPinningState,
): string {
  const pinned = column.getIsPinned();
  if (!pinned) return "";
  const edge =
    pinned === "start"
      ? pinning.start?.at(-1) === column.id && "shadow-[inset_-1px_0_0_var(--border)]"
      : pinning.end?.[0] === column.id && "shadow-[inset_1px_0_0_var(--border)]";
  return cn("bg-background", edge);
}

function ariaSort(sorted: false | "asc" | "desc"): "ascending" | "descending" | undefined {
  if (sorted === "asc") return "ascending";
  if (sorted === "desc") return "descending";
  return undefined;
}

/** The id TanStack will give a column def: its `id`, else its `accessorKey`. */
function columnDefId<TData extends RowData>(
  column: ColumnDef<DataTableFeatures, TData, unknown>,
): string {
  if (column.id) return column.id;
  if ("accessorKey" in column && typeof column.accessorKey === "string") return column.accessorKey;
  throw new Error("A hidden column needs an id or a string accessorKey");
}

function columnLabel<TData extends RowData>(
  column: Column<DataTableFeatures, TData, unknown>,
): string {
  const meta = column.columnDef.meta;
  if (meta?.label) return meta.label;
  const header = column.columnDef.header;
  return typeof header === "string" ? header : column.id;
}

function SortableHeader<TData extends RowData>({
  column,
  children,
}: {
  column: Column<DataTableFeatures, TData, unknown>;
  children: ReactNode;
}) {
  if (!column.getCanSort()) return <>{children}</>;
  const sorted = column.getIsSorted();
  return (
    <button
      type="button"
      className="-mx-1 inline-flex h-7 items-center gap-1 rounded px-1 hover:bg-muted"
      onClick={column.getToggleSortingHandler()}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUpIcon className="size-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDownIcon className="size-3.5" />
      ) : (
        <ChevronsUpDownIcon className="size-3.5 text-muted-foreground/60" />
      )}
    </button>
  );
}

type FilterableColumn<TData extends RowData> = Column<DataTableFeatures, TData, unknown>;

/** The columns the filter builder offers: those marked `meta.filter`, in column order. */
function filterableColumns<TData extends RowData>(
  table: TanstackTable<DataTableFeatures, TData>,
): FilterableColumn<TData>[] {
  return table.getAllLeafColumns().filter((column) => column.columnDef.meta?.filter !== undefined);
}

function operatorsFor<TData extends RowData>(
  column: FilterableColumn<TData>,
): DataTableFilterOperator[] {
  return column.columnDef.meta?.filter === "number"
    ? NUMBER_FILTER_OPERATORS
    : TEXT_FILTER_OPERATORS;
}

function FilterRow<TData extends RowData>({
  columns,
  column,
  filter,
}: {
  columns: FilterableColumn<TData>[];
  column: FilterableColumn<TData>;
  filter: ColumnFilter;
}) {
  const operators = operatorsFor(column);
  const columnItems = Object.fromEntries(columns.map((c) => [c.id, columnLabel(c)]));
  const operatorItems = Object.fromEntries(operators.map((o) => [o.id, o.label]));

  function moveTo(columnId: string) {
    const next = columns.find((c) => c.id === columnId);
    if (!next || next.id === column.id) return;
    const operator = operatorsFor(next)[0];
    if (!operator) return;
    column.setFilterValue(columnFilters(column.getFilterValue()).filter((f) => f.id !== filter.id));
    next.setFilterValue([
      ...columnFilters(next.getFilterValue()),
      { ...filter, operator, input: operator.value === "empty" ? "" : filter.input },
    ]);
  }

  function update(patch: Partial<ColumnFilter>) {
    column.setFilterValue(
      columnFilters(column.getFilterValue()).map((f) =>
        f.id === filter.id ? { ...f, ...patch } : f,
      ),
    );
  }

  function remove() {
    column.setFilterValue(columnFilters(column.getFilterValue()).filter((f) => f.id !== filter.id));
  }

  return (
    <div className="flex items-center gap-2">
      <Select
        items={columnItems}
        value={column.id}
        onValueChange={(value) => value && moveTo(value)}
      >
        <SelectTrigger size="sm" className="w-36" aria-label="Column">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {columns.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {columnLabel(c)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        items={operatorItems}
        value={filter.operator.id}
        onValueChange={(value) => {
          const operator = operators.find((o) => o.id === value);
          if (operator) update({ operator, input: operator.value === "empty" ? "" : filter.input });
        }}
      >
        <SelectTrigger size="sm" className="w-32" aria-label="Operator">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operators.map((operator) => (
            <SelectItem key={operator.id} value={operator.id}>
              {operator.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {filter.operator.value === "empty" ? (
        <span className="w-40" />
      ) : (
        <Input
          className="h-8 w-40"
          type={filter.operator.value === "number" ? "number" : "text"}
          inputMode={filter.operator.value === "number" ? "decimal" : undefined}
          placeholder={column.columnDef.meta?.filterCents ? "Dollars" : "Value"}
          value={filter.input}
          aria-label="Filter value"
          onChange={(event) => update({ input: event.target.value })}
        />
      )}
      <Button size="icon" variant="ghost" aria-label="Remove filter" onClick={remove}>
        <XIcon />
      </Button>
    </div>
  );
}

function FilterBuilder<TData extends RowData>({
  table,
}: {
  table: TanstackTable<DataTableFeatures, TData>;
}) {
  const [open, setOpen] = useState(false);
  const columns = filterableColumns(table);
  const active = columns.flatMap((column) =>
    columnFilters(column.getFilterValue()).map((filter) => ({ column, filter })),
  );
  if (columns.length === 0) return null;

  function add() {
    const column = columns[0];
    const operator = column ? operatorsFor(column)[0] : undefined;
    if (!column || !operator) return;
    column.setFilterValue([
      ...columnFilters(column.getFilterValue()),
      { id: crypto.randomUUID(), operator, input: "" },
    ]);
    setOpen(true);
  }

  return (
    <>
      <Button
        size="sm"
        variant={active.length > 0 ? "secondary" : "outline"}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <FilterIcon data-icon="inline-start" />
        Filter{active.length > 0 ? ` (${active.length})` : ""}
      </Button>
      {open ? (
        <div className="flex basis-full flex-col gap-2 rounded-md border border-border p-2">
          {active.length === 0 ? (
            <p className="text-sm text-muted-foreground">No filters. Add one to narrow the rows.</p>
          ) : (
            active.map(({ column, filter }) => (
              <FilterRow key={filter.id} columns={columns} column={column} filter={filter} />
            ))
          )}
          <div>
            <Button size="sm" variant="ghost" onClick={add}>
              <PlusIcon data-icon="inline-start" />
              Add filter
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function exportCsv<TData extends RowData>(
  table: TanstackTable<DataTableFeatures, TData>,
  name: string,
) {
  const columns = table
    .getAllLeafColumns()
    .filter((column) => column.accessorFn !== undefined && column.columnDef.meta?.csv !== false);
  const header = columns.map((column) => columnLabel(column));
  const body = table.getRowModel().rows.map((row: Row<DataTableFeatures, TData>) =>
    columns.map((column) => {
      const value = row.getValue(column.id);
      const format = column.columnDef.meta?.csv;
      return typeof format === "function" ? format(value) : value;
    }),
  );
  downloadCsv(`${name}.csv`, toCsv([header, ...body]));
}

export function DataTable<TData extends RowData>({
  columns,
  data,
  getRowId,
  pinned,
  rowSelection,
  onRowSelectionChange,
  search = "Search",
  csvName,
  toolbar,
  emptyMessage = "Nothing to show.",
  maxHeight,
  className,
}: DataTableProps<TData>) {
  const initialState = useMemo(
    () => ({
      columnPinning: { start: pinned?.start ?? [], end: pinned?.end ?? [] },
      columnVisibility: Object.fromEntries(
        columns
          .filter((column) => column.meta?.hidden)
          .map((column) => [columnDefId(column), false]),
      ),
    }),
    [pinned, columns],
  );
  const table = useTable<DataTableFeatures, TData>({
    features: dataTableFeatures,
    columns,
    data,
    getRowId,
    initialState,
    enableSortingRemoval: true,
    columnResizeMode: "onChange",
    defaultColumn: { minSize: 60, filterFn: dataTableFilterFn },
    globalFilterFn: "includesString",
    ...(rowSelection
      ? {
          state: { rowSelection },
          onRowSelectionChange: (updater) =>
            onRowSelectionChange?.(
              typeof updater === "function" ? updater(rowSelection ?? EMPTY_SELECTION) : updater,
            ),
        }
      : {}),
  });

  const rows = table.getRowModel().rows;
  const state: TableState<DataTableFeatures> = table.state;
  const pinning = state.columnPinning;
  const hasToolbar = search !== false || filterableColumns(table).length > 0 || csvName || toolbar;

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {hasToolbar ? (
        <div className="flex flex-wrap items-center gap-2">
          {search !== false ? (
            <Input
              className="h-8 w-56"
              placeholder={search}
              value={String(state.globalFilter ?? "")}
              onChange={(event) => table.setGlobalFilter(event.target.value)}
              aria-label={search}
            />
          ) : null}
          <FilterBuilder table={table} />
          <div className="ml-auto flex items-center gap-2">
            {toolbar}
            {csvName ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => exportCsv(table, csvName)}
                disabled={rows.length === 0}
              >
                <DownloadIcon data-icon="inline-start" />
                Download CSV
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div>
        <Table
          className="table-fixed"
          style={{ width: table.getTotalSize(), minWidth: "100%" }}
          containerClassName={maxHeight === undefined ? undefined : "overflow-y-auto"}
          containerStyle={maxHeight === undefined ? undefined : { maxHeight }}
        >
          {/* Sticky needs its own background and sits above the pinned cells (z 1) in the body.
              Collapsed table borders scroll away with the rows, so the rule under the header is
              a shadow, as it is on the pinned edge. */}
          <TableHeader
            className={
              maxHeight === undefined
                ? undefined
                : "sticky top-0 z-20 bg-background shadow-[inset_0_-1px_0_var(--border)]"
            }
          >
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    aria-sort={ariaSort(header.column.getIsSorted())}
                    style={{ ...pinnedStyle(header.column), width: header.getSize() }}
                    className={cn(
                      "relative overflow-hidden text-ellipsis",
                      pinnedClass(header.column, pinning),
                      header.column.columnDef.meta?.align === "end" && "text-right",
                    )}
                  >
                    {header.isPlaceholder ? null : (
                      <SortableHeader column={header.column}>
                        <table.FlexRender header={header} />
                      </SortableHeader>
                    )}
                    {header.column.getCanResize() ? (
                      <button
                        type="button"
                        aria-label={`Resize ${columnLabel(header.column)}`}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onDoubleClick={() => header.column.resetSize()}
                        className={cn(
                          "absolute inset-y-0 right-0 z-10 w-2 cursor-col-resize touch-none select-none border-r border-border hover:border-ring",
                          header.column.getIsResizing() && "border-ring",
                        )}
                      />
                    ) : null}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} data-state={row.getIsSelected() ? "selected" : undefined}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{ ...pinnedStyle(cell.column), width: cell.column.getSize() }}
                    className={cn(
                      "overflow-hidden text-ellipsis",
                      pinnedClass(cell.column, pinning),
                      cell.column.columnDef.meta?.align === "end" && "text-right",
                    )}
                  >
                    <table.FlexRender cell={cell} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {/* Outside the table: a cell spanning columns wider than the page would center the
            message on the columns, off to one side of what is shown. */}
        {rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A checkbox column: the header toggles every filtered row, a cell toggles its row, Shift-click
 * selects a range. Pin it to `start`.
 */
export function selectionColumn<TData extends RowData>(): ColumnDef<
  DataTableFeatures,
  TData,
  unknown
> {
  return {
    id: "select",
    size: 36,
    enableSorting: false,
    enableGlobalFilter: false,
    header: ({ table }) => (
      <input
        type="checkbox"
        aria-label="Select all"
        checked={table.getIsAllRowsSelected()}
        ref={(el) => {
          if (el) el.indeterminate = table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected();
        }}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <input
        type="checkbox"
        aria-label="Select row"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
  };
}
